import React, { useState } from 'react';

const staticFields = [
  { model: 'Subscription', app: 'subscription', field: 'name', label: 'subscription name' },
  { model: 'Personal', app: 'personal', field: 'first_name', label: 'person name' },
];

const CreateWATemplate = ({ onBack }) => {
  const [formData, setFormData] = useState({
    name: '',
    channel: 'whatsapp',
    body: '',
    variables: [],
    template_id: '',
    template_name: '',
    campaign_id: '',
  });

  const [loading, setLoading] = useState(false);
  const [successMsg, setSuccessMsg] = useState('');
  const [errorMsg, setErrorMsg] = useState('');

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleVariableChange = (index, selectedLabel) => {
    const selectedField = staticFields.find((f) => f.label === selectedLabel);
    const updatedVariables = [...formData.variables];

    if (selectedField) {
      updatedVariables[index] = {
        model: selectedField.model,
        app: selectedField.app,
        field: selectedField.field,
        label: selectedField.label,
      };
    } else {
      updatedVariables[index] = { model: '', app: '', field: '', label: '' };
    }

    setFormData((prev) => ({ ...prev, variables: updatedVariables }));
  };

  const handleAddVariable = () => {
    setFormData((prev) => ({
      ...prev,
      variables: [...prev.variables, { model: '', app: '', field: '', label: '' }],
    }));
  };

  const handleDeleteVariable = (index) => {
    const updatedVariables = formData.variables.filter((_, i) => i !== index);
    setFormData((prev) => ({ ...prev, variables: updatedVariables }));
  };

  const handleSave = async () => {
    setSuccessMsg('');
    setErrorMsg('');

    if (!formData.name.trim()) {
      setErrorMsg('Name is required.');
      return;
    }

    if (!formData.channel) {
      setErrorMsg('Channel is required.');
      return;
    }

    if (formData.channel === 'whatsapp') {
      if (!formData.template_name.trim()) {
        setErrorMsg('Template Name is required for WhatsApp.');
        return;
      }

      const allVariablesValid = formData.variables.every(
        (v) => v.model && v.app && v.field
      );

      if (!allVariablesValid) {
        setErrorMsg('All variable rows must have Model, App, and Field filled for WhatsApp.');
        return;
      }
    }

    if (formData.channel === 'sms') {
      if (!formData.template_id.trim()) {
        setErrorMsg('Template ID is required for SMS.');
        return;
      }
      if (!formData.campaign_id.trim()) {
        setErrorMsg('Campaign ID is required for SMS.');
        return;
      }
    }

    setLoading(true);

    const payload = {
      name: formData.name,
      channel: formData.channel,
      body: formData.body,
      variables: formData.variables.map(({ model, app, field }) => ({ model, app, field })),
    };

    if (formData.channel === 'sms') {
      payload.template_id = Number(formData.template_id);
      payload.campaign_id = formData.campaign_id;
    } else if (formData.channel === 'whatsapp') {
      payload.template_name = formData.template_name;
    }

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/eci/apis/v4/templates/create_watemplate`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${localStorage.getItem('authToken')}`,
          },
          body: JSON.stringify(payload),
        }
      );

      if (!response.ok) throw new Error('Failed to create template');

      setSuccessMsg('Template created successfully.');
      setFormData({
        name: '',
        channel: 'whatsapp',
        body: '',
        variables: [],
        template_id: '',
        template_name: '',
        campaign_id: '',
      });
    } catch (error) {
      setErrorMsg('Failed to create template.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-4">
      <button
        onClick={onBack}
        className="mb-4 bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600"
      >
        ← Back
      </button>

      <h2 className="text-2xl font-semibold mb-4">Create WA Template</h2>

      {successMsg && <p className="text-green-600 mb-4">{successMsg}</p>}
      {errorMsg && <p className="text-red-600 mb-4">{errorMsg}</p>}

      <div className="space-y-4">
        <InputField label="Name" name="name" value={formData.name} onChange={handleChange} />

        <div>
          <label className="block font-medium">Channel:</label>
          <select
            name="channel"
            value={formData.channel}
            onChange={handleChange}
            className="border rounded px-3 py-2 w-full"
          >
            <option value="whatsapp">WhatsApp</option>
            <option value="sms">SMS</option>
          </select>
        </div>

        {formData.channel === 'sms' && (
          <>
            <InputField
              label="Template ID"
              name="template_id"
              value={formData.template_id}
              onChange={handleChange}
            />
            <InputField
              label="Campaign ID"
              name="campaign_id"
              value={formData.campaign_id}
              onChange={handleChange}
            />
          </>
        )}

        {formData.channel === 'whatsapp' && (
          <InputField
            label="Template Name"
            name="template_name"
            value={formData.template_name}
            onChange={handleChange}
          />
        )}

        <div>
          <label className="block font-medium">Body:</label>
          <textarea
            name="body"
            value={formData.body}
            onChange={handleChange}
            className="border rounded px-3 py-2 w-full"
            rows={2}
          />
        </div>

        <div className="flex justify-between items-center">
          <label className="block font-medium">Variables:</label>
          <button
            onClick={handleAddVariable}
            className="mt-2 bg-blue-500 text-white px-3 py-1 rounded hover:bg-blue-600"
          >
            + Add Variable
          </button>
        </div>

        {formData.variables.map((variable, index) => (
          <div key={index} className="grid grid-cols-1 gap-2 mt-2 items-center">
            <div className="flex gap-2">
              <select
                value={variable.label}
                onChange={(e) => handleVariableChange(index, e.target.value)}
                className="border rounded px-3 py-2 w-full"
              >
                <option value="">Select Field</option>
                {staticFields.map((f) => (
                  <option key={f.label} value={f.label}>
                    {f.label}
                  </option>
                ))}
              </select>
              <button
                onClick={() => handleDeleteVariable(index)}
                className="bg-red-500 text-white px-2 py-1 rounded hover:bg-red-600"
                title="Delete Variable"
              >
                ✕
              </button>
            </div>
          </div>
        ))}
      </div>

      <button
        onClick={handleSave}
        disabled={loading}
        className="mt-6 bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700"
      >
        {loading ? 'Saving...' : 'Save'}
      </button>
    </div>
  );
};

const InputField = ({ label, name, value, onChange }) => (
  <div>
    <label className="block font-medium">{label}:</label>
    <input
      name={name}
      value={value}
      onChange={onChange}
      className="border rounded px-3 py-2 w-full"
    />
  </div>
);

export default CreateWATemplate;
